<template>
  <form
    id="call-log-form">
    <v-container
      class="h-100 d-flex flex-column pt-0">
      <!-- Timer -->
      <v-row
        v-if="callState === 'ongoing'"
        class="flex-grow-0">
        <v-col class="py-0">
          <h3>Open Call - {{duration}}</h3>
        </v-col>
      </v-row>
      <!-- / Timer -->

      <!-- Form Inputs -->
      <v-row
        class="flex-grow-0">
        <!-- Customer -->
        <v-col :cols="isMobile ? 10 : 'default'"
          v-if="isPermittedAll([
            'CustomersGetAll',
          ])"
          :class="{'pt-0': callState !== 'ongoing'}">
          <Autocomplete
            id="call-log-customer"
            label="Customer:"
            :items="$store.state.allCustomers"
            v-model="customer"
            item-value="CustomerID"
            item-text="CustomerName"
            :disabled="callState === 'ended'"
            @input="handleNewCustomerSet">
            <template v-slot:item="{ item }">
              <div class="w-100 d-flex align-center">
                <div class="mr-auto flex height-100 space-between width-50">
                  {{item.CustomerName}}
                </div>
                <p class="ended-support-contract margin-right-20"
                    v-if="item.CustomerStatusColor.CustomerStatusColorName == 'red'">
                    Do Not Support
                </p>
                <span
                  class="customer-status-dot"
                  :class="item.CustomerStatusColor.CustomerStatusColorName">
                </span>
              </div>
            </template>
          </Autocomplete>
        </v-col>
        <v-col 
          v-if="isPermittedAll([
            'CustomersCreate',
          ])"
          cols="auto"
          class="pl-0">
          <v-btn
            class="square"
            title="Add Customer"
            :class="{
              'mt-3': callState !== 'ongoing',
              'mt-6': callState === 'ongoing'
            }"
            type="button"
            color="primary"
            block
            :disabled="$store.getters.isLoadingActive"
            @click="$store.dispatch('openCustomerModalForm')">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
        <!-- / Customer -->

        <!-- Employee -->
        <v-col 
          v-if="isPermittedAll([
            'EmployeesGetAll',
          ])"
          :cols="isMobile ? 10 : 4"
          :class="{'pt-0': callState !== 'ongoing'}">
          <Autocomplete
            id="call-log-employee"
            label="Employee:"
            :items="allEmployees"
            v-model="employee"
            item-value="EmployeeName"
            item-text="EmployeeName"
            :disabled="callState === 'ended'"
            @input="findMatchingCustomerByEmployee">
          </Autocomplete>
        </v-col>

        <v-col 
          v-if="isPermittedAll([
            'EmployeesCreate',
          ])"
          cols="auto"
          class="pl-0">
          <v-btn
            class="square"
            title="Add Employee"
            :class="{
              'mt-3': callState !== 'ongoing',
              'mt-6': callState === 'ongoing'
            }"
            type="button"
            color="primary"
            block
            @click="$store.dispatch(
              'openEmployeeModalForm',
              {
                employee: {
                  CustomerID: activeCall.CustomerID,
                },
              },
            )"
            :disabled="$store.getters.isLoadingActive
              || !activeCall.Customer">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
        <!-- / Employee -->
      </v-row>


      <!-- Form Inputs -->
      <v-row
        class="flex-grow-0 space-between">
        <!-- Computer Serial Number -->
        <v-col :cols="isMobile ? 10 : 'default'"
          v-if="isPermittedAll([
            'ComputersGetAll',
          ])"
          :class="{'pt-0': callState !== 'ongoing'}">
          <Autocomplete
            id="call-log-computer"
            label="Computer Serial Number:"
            :items="allComputers"
            v-model="computer"
            item-value="ComputerSerialNumber"
            item-text="SerialNumber"
            :disabled="callState === 'ended'"
            @input="findMatchingCustomerByComputer">
          </Autocomplete>
        </v-col>
        <v-col
          cols="auto"
          class="pl-0 invisible">
          <v-btn
            class="square"
            title="Add Customer"
            :class="{
              'mt-3': callState !== 'ongoing',
              'mt-6': callState === 'ongoing'
            }"
            type="button"
            color="primary"
            block>
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
        <!-- / Computer Serial Number -->

        <!-- Employee Phone Number -->
        <v-col
          :cols="isMobile ? 10 : 4"
          :class="{'pt-0': callState !== 'ongoing'}">
          <Autocomplete
            id="call-log-employee-phone-number"
            label="Employee Phone Number:"
            :items="allEmployees"
            v-model="employee"
            item-text="EmployeePhone1"
            :disabled="callState === 'ended'"
            @input="findMatchingEmployeeByPhoneNumber">
          </Autocomplete>
        </v-col>
        <v-col
          cols="auto"
          class="pl-0 invisible">
          <v-btn
            class="square"
            title="Add Customer"
            :class="{
              'mt-3': callState !== 'ongoing',
              'mt-6': callState === 'ongoing'
            }"
            type="button"
            color="primary"
            block>
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
        <!-- / Employee Phone Number -->
      </v-row>

      <!-- Notes -->
          <v-row>
            <v-col
              class="d-flex flex-column">
              <label
                for="new-call-customer-notes">
                Customer Notes:
              </label>
              <v-textarea
                id="new-call-customer-notes"
                class="pt-0 mt-0"
                rows="3"
                v-model="customer.CustomerNotes"
                :readonly="true"
                hide-details
                no-resize>
              </v-textarea>
            </v-col>
          </v-row>
          <!-- Notes -->

      <!-- Start Call -->
      <v-row
        v-if="callState === 'new'
          && isPermittedAll([
            'CallsCreate',
          ])"
        class="flex-grow-1">
        <v-col cols="3">
          <v-btn
            type="submit"
            color="success"
            @click="startCall"
            :disabled="$store.getters.isLoadingActive"
            block>
            Start Call
          </v-btn>
        </v-col>
      </v-row>
      <!-- / Start Call -->

      <!-- Employees -->
      <v-row
        v-if="callState !== 'new'
          && isPermittedAll([
            'EmployeesGetAll',
          ])"
        class="flex-grow-0">
        <v-col>
          <CustomerEmployeesFormTable
            :customer-id="activeCall.CustomerID"
            :hide-add-button="true"
          />
        </v-col>
      </v-row>
      <!-- / Employees -->

      <!-- Computers -->
      <v-row
        v-if="callState !== 'new'
          && isPermittedAll([
            'ComputersGetAll',
          ])"
        class="flex-grow-0">
        <v-col>
          <CustomerComputersFormTable
            :customer-id="activeCall.CustomerID"
          />
        </v-col>
      </v-row>
      <!-- / Computers -->

      <v-row
        v-if="callState !== 'new'"
        class="flex-grow-1">
        <v-col
          :cols="isMobile ? 12 : 6"
          class="pt-0">
          <!-- Call Type -->
          <v-row>
            <v-col :cols="isMobile ? 12 : 6">
              <Autocomplete
                id="call-log-type"
                label="Call Type:"
                v-model="type"
                :items="$store.state.callTypes"
                item-value="CallTypeID"
                item-text="CallTypeName"
                :disabled="callState === 'ended'">
              </Autocomplete>
            </v-col>
          </v-row>
          <!-- / Call Type -->

          <!-- Problem -->
          <v-row>
            <v-col>
              <v-text-field
                id="call-log-problem"
                label="Problem:"
                v-model="activeCall.CallProblem"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Problem -->

          <!-- Notes -->
          <v-row>
            <v-col
              class="d-flex flex-column">
              <label
                for="call-log-notes">
                Notes:
              </label>
              <v-textarea
                id="call-log-notes"
                class="pt-0 mt-0"
                rows="3"
                v-model="activeCall.CallNotes"
                :readonly="$store.getters.isLoadingActive"
                hide-details
                no-resize>
              </v-textarea>
            </v-col>
          </v-row>
          <!-- Notes -->

          <!-- Tech / Openend / Closed -->
          <v-row
            v-if="callState === 'ended'">
            <v-col :cols="isMobile ? 12 : 6">
              <label for="call-log-tech">Tech:</label>
              <v-text-field
                id="call-log-tech"
                :value="activeCall.CreatedBy.TransactUser.UserGivenName"
                outlined
                filled
                disabled
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row
            v-if="callState === 'ended'">
            <v-col :cols="isMobile ? 12 : 6">
              <label for="call-log-opened">Opened:</label>
              <v-text-field
                id="call-log-opened"
                :value="activeCall.CallStartUtc | dateAtTime"
                outlined
                filled
                disabled
                hide-details>
              </v-text-field>
            </v-col>
            <v-col :cols="isMobile ? 12 : 6">
              <label for="call-log-closed">Closed:</label>
              <v-text-field
                id="call-log-closed"
                :value="activeCall.CallEndUtc | dateAtTime"
                outlined
                filled
                disabled
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Tech / Openend / Closed -->

          <!-- Status -->
          <v-row>
            <v-col>
              <Autocomplete
                id="call-log-status"
                label="Status:"
                :items="$store.state.callStatuses"
                v-model="status"
                item-value="CallStatusID"
                item-text="CallStatusName"
                :disabled="callState === 'ended'">
              </Autocomplete>
            </v-col>

            <v-col
              v-if="callState !== 'ended'
                && isPermittedAll([
                  'CallsUpdate',
                ])"
              cols="auto">
              <v-btn
                class="mt-6"
                type="button"
                color="error"
                @click="endCall"
                :disabled="$store.getters.isLoadingActive"
                block>
                End Call
              </v-btn>
            </v-col>
          </v-row>
          <!-- / Status -->
        </v-col>

        <!-- History -->
        <v-col
          id="call-log-history-container"
          :cols="isMobile ? 12 : 6"
          class="d-flex flex-column pt-3">
          <label
            class="flex-grow-0"
            for="call-log-history">
            History:
          </label>
          <v-textarea
            id="call-log-history"
            class="flex-grow-1"
            :value="callHistory"
            readonly
            outlined
            filled
            hide-details
            no-resize>
          </v-textarea>
        </v-col>
        <!-- History -->
      </v-row>
      <!-- / Form Inputs -->

      <!-- Form Buttons -->
       <v-row>
        <v-col class="d-flex justify-end">
          <LastModifiedBy
            class="order-first ml-5"
            :entity="activeCall"
          />
        </v-col>
       </v-row>
      <v-row
        class="flex-grow-0">
        <v-col class="d-flex align-center">
          <v-btn
            v-if="(callState === 'ongoing'
                || callState === 'ended')
              && isPermittedAll([
                'CallsUpdate',
              ])"
            type="submit"
            class="ml-auto"
            color="primary"
            @click="saveCall"
            :disabled="$store.getters.isLoadingActive
              || !$store.getters.isActiveCallDirty">
            Save
          </v-btn>

          <v-btn
            @click="goBack"
            type="button"
            color="primary"
            class="order-first"
            outlined>
            Back
          </v-btn>

          <v-btn
            v-if="callState !== 'new'
              && isPermittedAll([
                'CallsDelete',
              ])
              && !activeCall.CallIsDeleted"
            type="button"
            color="error"
            class="order-first ml-5"
            @click="deleteCall"
            :disabled="$store.getters.isLoadingActive"
            outlined>
            Hide Call
          </v-btn>

          <v-btn
            v-if="callState !== 'new'
              && isPermittedAll([
                'CallsRestore',
              ])
              && activeCall.CallIsDeleted"
            type="button"
            color="error"
            class="order-first ml-5"
            @click="restoreCall"
            :disabled="$store.getters.isLoadingActive"
            outlined>
            Unhide Call
          </v-btn>
        </v-col>
      </v-row>
      <!-- / Form Buttons -->
    </v-container>
  </form>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import CustomerComputersFormTable from './CustomerComputersFormTable.vue';
import CustomerEmployeesFormTable from './CustomerEmployeesFormTable.vue';
import LastModifiedBy from './LastModifiedBy.vue';

export default {
  name: 'CallLogForm',
  components: {
    CustomerComputersFormTable,
    CustomerEmployeesFormTable,
    LastModifiedBy,
  },
  data() {
    return {
      duration: '',
      timer: null,
      customer: { CustomerStatusColor: {} },
      computer: {},
      employee: {},
    };
  },
  watch: {
    $route(newRoute) {
      const callID = parseInt(newRoute.params.CallID);
      if (!isNaN(callID)) {
        this.getActiveCall(callID);
      }
    },
    customer() {
      this.setActiveCallData();
    },
    computer() {
      this.setActiveCallData();
    },
    employee() {
      this.setActiveCallData();
    },
  },
  computed: {
    activeCall() {
      return this.$store.state.activeCall;
    },
    callState() {
      let state = 'new';

      if (this.activeCall.CallEndUtc) {
        state = 'ended';
      } else if (this.activeCall.CallID) {
        state = 'ongoing';
      }

      return state;
    },
    employeePhoneNumber: {
      get() {
        return this.activeCall.EmployeePhoneNumber;
      },
      set() {

      },
    },
    type: {
      get() {
        return this.activeCall.CallType;
      },
      set(type) {
        if (type) {
          this.activeCall.CallTypeName = _.clone(type);
          this.activeCall.CallTypeID = type.CallTypeID;
        }
      },
    },
    status: {
      get() {
        return this.activeCall.CallStatus;
      },
      set(status) {
        if (status) {
          this.activeCall.CallStatus = _.clone(status);
          this.activeCall.CallStatusID = status.CallStatusID;
        }
      },
    },
    callHistory() {
      let historyStr = '';

      _.each(this.activeCall.CustomerCallHistory, (history) => {
        historyStr += `${moment(history.CallStartUtc).format('M/D/YYYY [at] h:mm A')}\n`;
        historyStr += `Tech: ${history.CreatedBy.TransactUser.UserGivenName}\n`;

        if (history.Employee) {
          historyStr += `Name: ${history.Employee.EmployeeName}\n`;
        }

        if (history.ComputerID) {
          historyStr += `Computer: ${history.Computer.SerialNumber}\n`;
        }

        if (history.CallProblem) {
          historyStr += `${history.CallProblem}\n`;
        }

        if (history.CallNotes) {
          historyStr += `${history.CallNotes}\n`;
        }

        historyStr += '\n';
      });

      return historyStr;
    },
    allComputers() {
      let allComputers = [];
      this.$store.state.computers.forEach((listComputers) => {
        allComputers = allComputers.concat(listComputers);
      });

      if (this.customer != null && this.customer.CustomerID != null) {
        allComputers = allComputers.filter((c) => c.CustomerID === this.customer.CustomerID);
      }

      return allComputers;
    },
    allEmployees() {
      let allEmployees = this.$store.state.customerEmployeesForDropdown;

      if (this.customer != null && this.customer.CustomerID != null) {
        allEmployees = allEmployees.filter((e) => e.CustomerID === this.customer.CustomerID);
      }

      return allEmployees;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    }

  },
  methods: {
    getActiveCall(callID) {
      this.getCall(callID)
        .then(() => {
          this.getCustomerEmployeesAndComputers(this.activeCall.Customer);
          this.customer = this.activeCall.Customer;
          this.computer = this.activeCall.Computer;
          this.employee = this.activeCall.Employee;
        });
    },
    startCall() {
      this.setActiveCallData();
      const data = this.activeCall;

      this.$store.dispatch('createCall', { call: data });
    },
    saveCall() {
      this.$store.dispatch('updateCall', { call: this.activeCall });
    },
    endCall() {
      this.$store.dispatch('endCall', { call: this.activeCall });
    },
    deleteCall() {
      const confirm = () => {
        this.$store.dispatch('deleteCall', { call: this.activeCall })
          .then(() => {
            this.$router.push({
              name: 'CallLogPage',
            });
          });
      };

      this.showConfirm(
        'Are you sure you want to hide this call?',
        'Hide Call',
      ).then(confirm, _.noop);
    },
    restoreCall() {
      const confirm = () => {
        this.$store.dispatch('restoreCall', { call: this.activeCall })
          .then(() => {
            this.$router.push({
              name: 'CallLogPage',
            });
          });
      };

      this.showConfirm(
        'Are you sure you want to unhide this call?',
        'Unhide Call',
      ).then(confirm, _.noop);
    },
    startTimer() {
      this.timer = setInterval(() => {
        const diff = moment().diff(this.activeCall.CallStartUtc);
        const duration = moment.duration(diff);
        let strDuration = '';

        let seconds = duration.seconds();
        seconds = seconds > 9
          ? seconds
          : `0${seconds}`;

        strDuration = `${seconds}s`;

        let minutes = duration.minutes();
        if (minutes > 0) {
          minutes = minutes > 9
            ? minutes
            : `0${minutes}`;

          strDuration = `${minutes}m ${strDuration}`;
        }

        let hours = duration.hours();
        if (hours > 0) {
          hours = hours > 9
            ? hours
            : `0${hours}`;

          strDuration = `${hours}h ${strDuration}`;
        }

        this.duration = strDuration;
      }, 100);
    },
    stopTimer() {
      clearInterval(this.timer);
    },
    getCustomerEmployeesAndComputers(customer) {
      if (customer.CustomerID) {
        this.getCustomerEmployeesForDropdown(customer);

        this.$store.dispatch('getCustomerComputersForDropdown', {
          params: {
            CustomerID: customer.CustomerID,
            HiddenStatus: 'ShowNotHidden',
          },
        });
      }
    },
    handleNewCustomerSet(customer) {
      this.customer = customer;
      this.employee = undefined;
      this.employeePhoneNumber = undefined;
      this.computer = undefined;
    },
    getCustomerEmployeesForDropdown(customer) {
      if (customer.CustomerID) {
        this.$store.dispatch('getCustomerEmployeesForDropdown', {
          params: {
            CustomerID: customer.CustomerID,
            HiddenStatus: 'ShowNotHidden',
          },
        });
      }
    },
    findMatchingCustomerByComputer(computer) {
      const newCustomer = this.getCustomer(computer.CustomerID);

      if (newCustomer.CustomerID !== this.customer.CustomerID) {
        this.employee = undefined;
        this.customer = newCustomer;
      }
    },
    findMatchingCustomerByEmployee(employee) {
      const newCustomer = this.getCustomer(employee.CustomerID);

      if (this.customer === undefined) {
        this.customer = newCustomer;
      } else if (this.customer.CustomerID !== newCustomer.CustomerID) {
        this.customer = newCustomer;
        this.computer = undefined;
      }
    },
    findMatchingEmployeeByPhoneNumber(employee) {
      const newEmployee = this.getEmployee(employee.EmployeePhone1);
      const newCustomer = this.getCustomer(newEmployee.CustomerID);

      this.employee = newEmployee;
      if (this.customer === undefined) {
        this.customer = newCustomer;
      } else if (this.customer.CustomerID !== newCustomer.CustomerID) {
        this.customer = newCustomer;
        this.computer = undefined;
      }
    },
    getCustomer(customerID) {
      return _.find(
        this.$store.state.allCustomers,
        { CustomerID: customerID },
      );
    },
    getEmployee(employeePhoneNumber) {
      return _.find(
        this.$store.state.customerEmployeesForDropdown,
        { EmployeePhone1: employeePhoneNumber },
      );
    },
    getComputer(computerID) {
      return _.find(
        this.$store.state.allComputers,
        { ComputerID: computerID },
      );
    },
    setActiveCallData() {
      if (this.customer !== undefined && this.customer !== null) {
        this.activeCall.CustomerID = this.customer.CustomerID;
        this.activeCall.Customer = this.customer;
      } else {
        this.activeCall.CustomerID = undefined;
        this.activeCall.Customer = undefined;
      }

      if (this.employee !== undefined && this.employee !== null) {
        this.activeCall.EmployeeID = this.employee.EmployeeID;
        this.activeCall.EmployeePhoneNumber = this.employee.EmployeePhone1;
      } else {
        this.activeCall.EmployeeID = undefined;
        this.activeCall.EmployeePhoneNumber = undefined;
      }

      if (this.computer !== undefined && this.computer !== null) {
        this.activeCall.ComputerID = this.computer.ComputerID;
      } else {
        this.activeCall.ComputerID = undefined;
      }
    },
  },
  created() {
    this.startTimer();

    if (this.$route.params.CallID) {
      this.getActiveCall(this.$route.params.CallID);
    }

    this.$store.dispatch('getOpenCalls');
    this.$store.dispatch('getAllCustomers');
    this.$store.dispatch('getCustomerEmployeesForDropdown');
    this.$store.dispatch('getComputers');
    this.$store.dispatch('getCallTypes');
    this.$store.dispatch('getCallStatuses');
  },
  destroyed() {
    this.stopTimer();
  },
};
</script>

<style lang="scss">
#call-log-form {
  overflow-y: auto;

  #call-log-history-container {
    fieldset {
      border: none;
    }

    .v-input__slot {
      padding: 0;

      &:hover {
        background-color: rgba(255, 255, 255, 0.08);
      }
    }

    textarea {
      margin-top: 0;
      padding: 4px 8px;
    }
  }
}

.v-application.theme--light {
  #call-log-form {
    #call-log-history-container {
      .v-input__slot {
        background-color: #eeeeee !important;
      }
    }
  }
}

.v-application.theme--dark {
  #call-log-form {
    #call-log-history-container {
      .v-input__slot {
        textarea {
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }
  }
}
</style>
